import React, { useState, useEffect } from 'react';
import type {
  JobBidIntent,
  SupplierCapacityAsk,
  ComputeLeaseContract,
  TrustTierLevel,
} from '../../mesh/depin/contracts/marketplace-contracts.js';

export interface DePINMarketplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  userBalanceMicroCredits?: number;
  onPlaceBid?: (bid: Partial<JobBidIntent>) => Promise<{ success: boolean; error?: string }>;
}

interface NodeCapacityCard {
  nodeId: string;
  tailscaleIp: string;
  cpuCores: number;
  memoryGiB: number;
  gpuModel?: string;
  vramGiB?: number;
  hasTee: boolean;
  trustTier: TrustTierLevel;
  carbonIntensity: number;
  hourlyRateCredits: number;
}

export const DePINMarketplaceModal: React.FC<DePINMarketplaceModalProps> = ({
  isOpen,
  onClose,
  userBalanceMicroCredits = 5000000, // 5 CREDITS default
  onPlaceBid,
}) => {
  const [activeTab, setActiveTab] = useState<'explore' | 'bid' | 'leases' | 'compliance'>('explore');
  const [nodes, setNodes] = useState<NodeCapacityCard[]>([]);
  const [loading, setLoading] = useState(true);

  // Bidding form state
  const [minCpu, setMinCpu] = useState(4);
  const [minRam, setMinRam] = useState(16);
  const [requireGpu, setRequireGpu] = useState(true);
  const [requireTee, setRequireTee] = useState(false);
  const [pricingModel, setPricingModel] = useState<'SPOT' | 'ON_DEMAND_HOURLY'>('SPOT');
  const [durationHours, setDurationHours] = useState(4);
  const [maxBudgetCredits, setMaxBudgetCredits] = useState(2.0);
  const [bidStatus, setBidStatus] = useState<string | null>(null);

  // Active Leases state
  const [activeLeases, setActiveLeases] = useState<ComputeLeaseContract[]>([
    {
      leaseId: 'lease_mock_4090_01',
      bidId: 'bid_ai_train_01',
      supplierNodeId: 'herxpc-1',
      buyerNodeId: 'herxpc',
      agreedRateMicroCreditsPerHour: 500000, // 0.5 CREDITS/hr
      startTime: Date.now() - 3600_000,
      expiresAt: Date.now() + 3 * 3600_000,
      streamIntervalMs: 300_000,
      status: 'RUNNING',
      teeMeasurementHash: 'sha256:7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069',
    },
  ]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Mock / Live fleet discovery
      setTimeout(() => {
        setNodes([
          {
            nodeId: 'herxpc-1',
            tailscaleIp: '100.107.223.61',
            cpuCores: 16,
            memoryGiB: 64,
            gpuModel: 'RTX 4090',
            vramGiB: 24,
            hasTee: true,
            trustTier: 'GOLD',
            carbonIntensity: 280,
            hourlyRateCredits: 0.5,
          },
          {
            nodeId: 'vps34815',
            tailscaleIp: '100.66.28.42',
            cpuCores: 8,
            memoryGiB: 32,
            hasTee: true,
            trustTier: 'PLATINUM',
            carbonIntensity: 190,
            hourlyRateCredits: 0.2,
          },
          {
            nodeId: 'ella-pc',
            tailscaleIp: '100.109.34.11',
            cpuCores: 8,
            memoryGiB: 32,
            hasTee: false,
            trustTier: 'SILVER',
            carbonIntensity: 350,
            hourlyRateCredits: 0.15,
          },
        ]);
        setLoading(false);
      }, 300);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBidSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBidStatus('Submitting bid to Double-Auction Order Book...');
    try {
      if (onPlaceBid) {
        await onPlaceBid({
          pricingModel,
          maxDurationHours: durationHours,
          maxRateMicroCreditsPerHour: Math.round(maxBudgetCredits * 1000000),
          hardware: {
            minCpuCores: minCpu,
            minRamGb: minRam,
            minVramGb: requireGpu ? 16 : undefined,
            minTrustTier: 'BRONZE',
            requireTee,
          },
        });
      }
      setBidStatus('Bid matched and Escrow Hold reserved successfully! Lease provisioned.');
    } catch (err: any) {
      setBidStatus(`Failed: ${err?.message || 'Bidding failed'}`);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="depin-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in"
    >
      <div className="relative w-full max-w-4xl rounded-2xl border border-cyan-500/30 bg-slate-950/90 text-slate-100 shadow-[0_0_50px_rgba(6,182,212,0.15)] flex flex-col overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800/80 px-6 py-4 bg-slate-900/50">
          <div className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 font-bold text-xl">
              ⚡
            </div>
            <div>
              <h2 id="depin-modal-title" className="text-lg font-bold tracking-tight text-white flex items-center gap-2">
                Sovereign Resource Mesh & Marketplace
                <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-xs font-semibold text-cyan-300 border border-cyan-500/30">
                  DePIN v3.1
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Continuous Double-Auction • 5-Min Escrow Stream • TEE Confidential Compute
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span id="active-escrow-label" data-testid="active-escrow-label" className="text-[11px] text-slate-400 block">Available Balance</span>
              <span data-testid="active-escrow-balance" className="text-sm font-mono font-bold text-emerald-400 [direction:ltr] [unicode-bidi:plaintext]">
                {(userBalanceMicroCredits / 1000000).toFixed(2)} HERX-CREDIT
              </span>
            </div>
            <button
              onClick={onClose}
              aria-label="Close Marketplace Dialog"
              className="min-w-[44px] min-h-[44px] p-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-800/80 bg-slate-900/30 px-6 gap-2 pt-2">
          {[
            { id: 'explore', label: 'Capacity Explorer', icon: '🌐' },
            { id: 'bid', label: 'Bidding & Intent Console', icon: '🎯' },
            { id: 'leases', label: 'Active Leases & Escrow', icon: '⏳' },
            { id: 'compliance', label: 'Compliance & Safety Badges', icon: '🛡️' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors flex items-center gap-2 border-b-2 ${
                activeTab === tab.id
                  ? 'border-cyan-400 text-cyan-300 bg-slate-800/40'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/20'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Tab 1: Capacity Explorer */}
          {activeTab === 'explore' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-slate-200">Verified Fleet Capacity Asks</h3>
                <span className="text-xs text-slate-400">Showing {nodes.length} available mesh nodes</span>
              </div>
              {loading ? (
                <div className="py-12 text-center text-sm text-slate-400">Discovering Tailscale fleet capacity...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {nodes.map((node) => (
                    <div
                      key={node.nodeId}
                      className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 hover:border-cyan-500/40 transition-all shadow-sm space-y-3"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-bold text-white text-sm">{node.nodeId}</span>
                          <span className="block font-mono text-[11px] text-cyan-400">{node.tailscaleIp}</span>
                        </div>
                        <div className="flex gap-1.5">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                              node.trustTier === 'PLATINUM'
                                ? 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                                : node.trustTier === 'GOLD'
                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                : 'bg-slate-700/40 text-slate-300 border-slate-600'
                            }`}
                          >
                            {node.trustTier}
                          </span>
                          {node.hasTee && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                              🔒 TEE
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-300 bg-slate-950/40 p-2.5 rounded-lg border border-slate-800/60">
                        <div>
                          <span className="text-slate-500 block text-[10px]">Compute</span>
                          {node.cpuCores} vCPUs • {node.memoryGiB} GB RAM
                        </div>
                        <div>
                          <span className="text-slate-500 block text-[10px]">Acceleration</span>
                          {node.gpuModel ? `${node.gpuModel} (${node.vramGiB}GB)` : 'None'}
                        </div>
                        <div>
                          <span className="text-slate-500 block text-[10px]">Grid Carbon</span>
                          🌱 {node.carbonIntensity} gCO₂e/kWh
                        </div>
                        <div>
                          <span className="text-slate-500 block text-[10px]">Rate</span>
                          <span className="text-cyan-300 font-bold">{node.hourlyRateCredits} CREDIT/hr</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Bidding & Intent Console */}
          {activeTab === 'bid' && (
            <form onSubmit={handleBidSubmit} className="space-y-4 max-w-xl mx-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Pricing Model</label>
                  <select
                    value={pricingModel}
                    onChange={(e) => setPricingModel(e.target.value as any)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-cyan-500"
                  >
                    <option value="SPOT">Spot Interruptible (60% Discount)</option>
                    <option value="ON_DEMAND_HOURLY">On-Demand Guaranteed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Duration (Hours)</label>
                  <input
                    type="number"
                    min={1}
                    max={72}
                    value={durationHours}
                    onChange={(e) => setDurationHours(Number(e.target.value))}
                    className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-cyan-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Min vCPUs</label>
                  <input
                    type="number"
                    min={1}
                    max={64}
                    value={minCpu}
                    onChange={(e) => setMinCpu(Number(e.target.value))}
                    className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Min Memory (GB)</label>
                  <input
                    type="number"
                    min={2}
                    max={256}
                    value={minRam}
                    onChange={(e) => setMinRam(Number(e.target.value))}
                    className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-cyan-500"
                  />
                </div>
              </div>

              <div className="flex gap-6 py-2">
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requireGpu}
                    onChange={(e) => setRequireGpu(e.target.checked)}
                    className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                  />
                  Require GPU Acceleration (RTX 4090 / CUDA)
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requireTee}
                    onChange={(e) => setRequireTee(e.target.checked)}
                    className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                  />
                  Require TEE Confidential Compute (SEV-SNP / TDX)
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">Max Budget (HERX-CREDIT)</label>
                <input
                  type="number"
                  step={0.1}
                  min={0.1}
                  value={maxBudgetCredits}
                  onChange={(e) => setMaxBudgetCredits(Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-cyan-500"
                />
              </div>

              <button
                type="submit"
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 font-bold text-xs text-white shadow-md hover:from-cyan-400 hover:to-blue-500 transition-all"
              >
                Submit Intent to Double-Auction
              </button>

              {bidStatus && (
                <div className="text-xs text-center p-2 rounded bg-slate-900 border border-cyan-500/30 text-cyan-300 font-mono">
                  {bidStatus}
                </div>
              )}
            </form>
          )}

          {/* Tab 3: Active Leases & Escrow */}
          {activeTab === 'leases' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-200">Active Compute Leases</h3>
              <div className="space-y-3">
                {activeLeases.map((lease) => (
                  <div
                    key={lease.leaseId}
                    className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 space-y-2"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-bold text-white text-sm">{lease.leaseId}</span>
                        <span className="block text-[11px] text-slate-400">
                          Supplier: <span className="text-cyan-300">{lease.supplierNodeId}</span> • Agreed Rate:{' '}
                          {(lease.agreedRateMicroCreditsPerHour / 1000000).toFixed(2)} CREDIT/hr
                        </span>
                      </div>
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                        {lease.status}
                      </span>
                    </div>

                    <div className="text-xs font-mono text-slate-400 bg-slate-950/60 p-2 rounded border border-slate-800/80">
                      🔒 Attestation: {lease.teeMeasurementHash?.substring(0, 32)}...
                    </div>

                    <div className="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-cyan-400 h-full w-1/4 animate-pulse" />
                    </div>
                    <span className="text-[10px] text-slate-500 block text-right">5-Min Escrow Stream Active</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab 4: Compliance & Safety Badges */}
          {activeTab === 'compliance' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40 space-y-2">
                <h4 className="font-bold text-emerald-400 flex items-center gap-1.5">
                  🔒 Zero-Trust Confidential Compute
                </h4>
                <p className="text-slate-400">
                  AMD SEV-SNP and Intel TDX enclaves encrypt guest memory and CPU state with 64-byte freshness nonces,
                  ensuring stranger nodes cannot view in-memory workloads.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40 space-y-2">
                <h4 className="font-bold text-cyan-400 flex items-center gap-1.5">
                  🌱 Carbon-Aware Green Scheduling
                </h4>
                <p className="text-slate-400">
                  Clamped green math optimizes workload scheduling against regional grid carbon intensity, issuing
                  Ed25519-signed ESG carbon-reduction certificates.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40 space-y-2">
                <h4 className="font-bold text-purple-400 flex items-center gap-1.5">
                  🛡️ Hardware Trust Tiers & SLA Slashing
                </h4>
                <p className="text-slate-400">
                  Uptime derived from verifiable challenge rounds; failing heartbeats immediately demotes nodes to
                  UNVERIFIED with a 14-day recovery probation.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40 space-y-2">
                <h4 className="font-bold text-amber-400 flex items-center gap-1.5">
                  📦 10-Shard Reed-Solomon Storage
                </h4>
                <p className="text-slate-400">
                  Files split across 10 mesh nodes with k=4 recovery threshold and 30-minute anti-storm quarantine
                  shields against reboot churn.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
